import { PageSize } from '@/constant';
import {
	GetWorkerAssignmentListParams,
	IWorkerAssignmentFranchiseListItem,
	IWorkerAssignmentListItem,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import { formatWorkerAssignmentPublicList } from './worker-assignment-public.helper';
import {
	IPayloadDeleteWorkerAssignmentPublicRequest,
	IPayloadGetWorkerAssignmentPublicApplicantListRequest,
	IPayloadGetWorkerAssignmentPublicApplicantListSuccess,
	IPayloadGetWorkerAssignmentPublicDetailRequest,
	IPayloadGetWorkerAssignmentPublicDetailSuccess,
	IPayloadGetWorkerAssignmentPublicExcelListRequest,
	IPayloadGetWorkerAssignmentPublicListRequest,
	IPayloadGetWorkerAssignmentPublicListSuccess,
	IWorkerAssignmentPublicApplicantListStore,
	IWorkerAssignmentPublicDetailStore,
	IWorkerAssignmentPublicListStore,
} from './worker-assignment-public.type';

const InitParams: GetWorkerAssignmentListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	workerAssignmentPublicList: [] as IWorkerAssignmentPublicListStore[],
	total: 0,
	params: InitParams,

	excelListFetching: false,
	detailFetching: false,
	workerAssignmentPublicDetail: {} as IWorkerAssignmentPublicDetailStore,

	applicantListFetching: false,
	workerAssignmentPublicApplicantList:
		[] as IWorkerAssignmentPublicApplicantListStore[],
	applicantTotal: 0,
	applicantParams: InitParams,
};

const workerAssignmentPublicSlice = createSlice({
	initialState,
	name: 'workerAssignmentPublic',
	reducers: {
		/**---- GET WORKER ASSIGNMENT FRANCHISE LIST START ---- */
		getWorkerAssignmentPublicListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentPublicListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getWorkerAssignmentPublicListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentPublicListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerAssignmentPublicList = content;
			state.total = total;
		},
		getWorkerAssignmentPublicListFailure: (state) => {
			state.fetching = false;
			state.workerAssignmentPublicList = [];
			state.total = 0;
		},

		getWorkerAssignmentPublicExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentPublicExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerAssignmentPublicExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerAssignmentPublicExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER ASSIGNMENT FRANCHISE LIST END ---- */

		/**---- UPDATE WORKER ASSIGNMENT FRANCHISE START ---- */
		updateWorkerAssignmentFranchiseListSuccess: (
			state,
			action: PayloadAction<
				TPayloadUpdateSuccess<IWorkerAssignmentFranchiseListItem>
			>
		) => {
			const { data } = action.payload;
			const workerAssignmentPublicList = [...state.workerAssignmentPublicList];
			state.workerAssignmentPublicList = formatWorkerAssignmentPublicList(
				mergeListItemUpdated<
					IWorkerAssignmentPublicListStore,
					IWorkerAssignmentListItem
				>(workerAssignmentPublicList, data)
			);
		},
		/**---- UPDATE WORKER ASSIGNMENT FRANCHISE END ---- */

		/**---- GET WORKER ASSIGNMENT PUBLIC DETAIL START ---- */
		getWorkerAssignmentPublicDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentPublicDetailRequest>
		) => {
			state.detailFetching = true;
		},
		getWorkerAssignmentPublicDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentPublicDetailSuccess>
		) => {
			state.detailFetching = false;
			state.workerAssignmentPublicDetail = action.payload.detail;
		},
		getWorkerAssignmentPublicDetailFailure: (state) => {
			state.detailFetching = false;
			state.workerAssignmentPublicDetail =
				{} as IWorkerAssignmentPublicDetailStore;
		},
		/**---- GET WORKER ASSIGNMENT PUBLIC DETAIL END ---- */

		/**---- GET WORKER ASSIGNMENT PUBLIC APPLICANT LIST START ---- */
		getWorkerAssignmentPublicApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentPublicApplicantListRequest>
		) => {
			state.applicantListFetching = true;
			state.applicantParams = action.payload;
		},
		getWorkerAssignmentPublicApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerAssignmentPublicApplicantListSuccess>
		) => {
			const { content, total } = action.payload;
			state.applicantListFetching = false;
			state.workerAssignmentPublicApplicantList = content;
			state.applicantTotal = total;
		},
		getWorkerAssignmentPublicApplicantListFailure: (state) => {
			state.applicantListFetching = false;
			state.workerAssignmentPublicApplicantList = [];
			state.applicantTotal = 0;
		},
		/**---- GET WORKER ASSIGNMENT PUBLIC APPLICANT LIST END ---- */

		/**---- DELETE WORKER ASSIGNMENT PUBLIC START ---- */
		deleteWorkerAssignmentPublicRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerAssignmentPublicRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerAssignmentPublicSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerAssignmentPublicFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER ASSIGNMENT PUBLIC END ---- */
	},
});

export const WorkerAssignmentPublicSelectors = {
	selectFetching: (state: RootState) => state.workerAssignmentPublic.fetching,
	selectParams: (state: RootState) => state.workerAssignmentPublic.params,
	selectList: (state: RootState) =>
		state.workerAssignmentPublic.workerAssignmentPublicList,
	selectTotal: (state: RootState) => state.workerAssignmentPublic.total,
	selectDetailFetching: (state: RootState) =>
		state.workerAssignmentPublic.detailFetching,
	selectDetail: (state: RootState) =>
		state.workerAssignmentPublic.workerAssignmentPublicDetail,
	selectApplicantListFetching: (state: RootState) =>
		state.workerAssignmentPublic.applicantListFetching,
	selectApplicantParams: (state: RootState) =>
		state.workerAssignmentPublic.applicantParams,
	selectApplicantList: (state: RootState) =>
		state.workerAssignmentPublic.workerAssignmentPublicApplicantList,
	selectApplicantTotal: (state: RootState) =>
		state.workerAssignmentPublic.applicantTotal,
};

export const WorkerAssignmentPublicActions =
	workerAssignmentPublicSlice.actions;
export const WorkerAssignmentPublicReducer =
	workerAssignmentPublicSlice.reducer;
